/**
 * AttachmentsCard — the Attachments section of File Detail, plus the
 * Adviser-only purge confirmation modal that acts on it (decomposition
 * step F6b, CLAUDE.md §8.4 / §29).
 *
 * Purged attachments (`file.attachmentsPurgedAt`) are kept in the list but
 * rendered inert — greyed, struck through, no link — with a
 * "Purged DD/MM/YYYY" tag, so there are no broken signed-URL links.
 *
 * The purge state (`showPurgeConfirm`, `purging`) and the write path
 * (`handleManualPurge`) stay in FileDetail.jsx because the Actions card's
 * purge button reads them too; the modal takes them as props. Both blocks
 * are extracted verbatim and keep their original indentation, and render at
 * their original call sites behind the view's own guards.
 */

import { motion } from 'framer-motion';
import { FileText, Download } from 'lucide-react';
import { formatDate } from '../../utils/dateHelpers';
import { styles as caseModalStyles } from './caseScreenshots/EditCasesModal';
import { sharedStyles } from './FileDetail.styles';

export default function AttachmentsCard({ file }) {
  return (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Attachments</h3>
                  {file.attachmentsPurgedAt && (
                    <p style={styles.attachmentPurgedNote}>
                      Working copies purged from storage on{' '}
                      {formatDate(file.attachmentsPurgedAt)} — originals remain in the CRM.
                    </p>
                  )}
                  <div style={styles.attachmentList}>
                    {file.attachments.map((url, idx) =>
                      file.attachmentsPurgedAt ? (
                        <span key={idx} style={styles.attachmentPurged}>
                          <FileText size={14} />
                          Attachment {idx + 1}
                          <span style={styles.attachmentPurgedTag}>
                            Purged {formatDate(file.attachmentsPurgedAt)}
                          </span>
                        </span>
                      ) : (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.attachmentLink}
                        >
                          <Download size={14} />
                          Attachment {idx + 1}
                        </a>
                      )
                    )}
                  </div>
                </div>
  );
}

/** Purge attachments confirmation modal (CLAUDE.md §29). */
export function PurgeConfirmModal({ file, purging, setShowPurgeConfirm, handleManualPurge }) {
  return (
        <div style={styles.modalOverlay} onClick={() => !purging && setShowPurgeConfirm(false)}>
          <motion.div
            className="card"
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--cp-warning)' }}>Purge attachments now</h3>
            <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Permanently delete the uploaded attachment files for this file from storage?
            </p>
            <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.75rem', color: 'var(--cp-text)' }}>
              {file.clientName}
            </p>
            <p style={{ color: 'var(--cp-warning)', fontSize: '0.8rem', marginBottom: '1rem' }}>
              This cannot be undone. The working copies are removed from storage — the
              originals remain in the CRM. The file record and its history are kept.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-outline"
                onClick={() => setShowPurgeConfirm(false)}
                disabled={purging}
              >
                Cancel
              </button>
              <button
                style={styles.confirmPurgeBtn}
                onClick={handleManualPurge}
                disabled={purging}
              >
                {purging ? 'Purging…' : 'Purge attachments'}
              </button>
            </div>
          </motion.div>
        </div>
  );
}

export const styles = {
  attachmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  attachmentLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 0.75rem',
    background: 'var(--cp-bg)',
    borderRadius: '8px',
    fontSize: '0.8rem',
    color: 'var(--cp-primary)',
    fontWeight: 500,
    textDecoration: 'none',
  },
  // Purged attachment — inert, greyed, no link (CLAUDE.md §29).
  attachmentPurged: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 0.75rem',
    background: 'var(--cp-bg)',
    borderRadius: '8px',
    fontSize: '0.8rem',
    color: '#9AA7B4',
    fontWeight: 500,
    textDecoration: 'line-through',
    cursor: 'default',
  },
  attachmentPurgedTag: {
    marginLeft: 'auto',
    paddingLeft: '0.5rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#9AA7B4',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  attachmentPurgedNote: {
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    fontStyle: 'italic',
    marginBottom: '0.5rem',
  },
  confirmPurgeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0.65rem 1.25rem',
    background: 'var(--cp-warning)',
    color: 'var(--cp-surface)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  // Shared with the FileDetail view (F7 seed, `FileDetail.styles.js`).
  section: sharedStyles.section,
  sectionTitle: sharedStyles.sectionTitle,
  // Shared modal chrome, owned by EditCasesModal (§8.4).
  modalOverlay: caseModalStyles.modalOverlay,
  modal: caseModalStyles.modal,
};
